/**
 * Projects Page
 *
 * Browse and filter all available projects.
 */

import { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { fetchProjects, searchProjects, followProject, unfollowProject } from '@/features/projects/api/projectsApi'
import { useAnalytics } from '@/lib/analytics'
import SavedSearches from '@/components/SavedSearches'
import SearchHistory from '@/components/SearchHistory'
import ProjectCard from '@/components/ProjectCard'
import { useAuth } from '@/features/auth/AuthContext'
import { supabase } from '@/lib/supabaseClient'
import { fetchMyReservations } from '@/features/reservations/api/reservationsApi'
import MobileSearchBar from '@/components/mobile/MobileSearchBar'
import FilterBottomSheet from '@/components/mobile/FilterBottomSheet'
import { usePullToRefresh } from '@/lib/usePullToRefresh'

export default function ProjectsPage() {
  const { user, profile } = useAuth()
  const { trackSearch, trackPageView } = useAnalytics()
  const queryClient = useQueryClient()
  const [searchQuery, setSearchQuery] = useState('')
  const [debouncedSearch, setDebouncedSearch] = useState('')
  const [showSearchHistory, setShowSearchHistory] = useState(false)
  const [showFilters, setShowFilters] = useState(false)
  const [showMobileFilters, setShowMobileFilters] = useState(false)
  const [filters, setFilters] = useState({
    location: '',
    minPrice: '',
    maxPrice: '',
    bedrooms: '',
    projectType: '',
    status: '',
    sortBy: 'newest',
  })

  const handleRefresh = async () => {
    await queryClient.invalidateQueries(['projects'])
    await queryClient.invalidateQueries(['search-projects'])
    await queryClient.invalidateQueries(['my-active-reservations'])
  }

  const { pullDistance, isRefreshing } = usePullToRefresh(handleRefresh)

  // Fetch buyer's active reservations to show "Reserved" badges
  const { data: reservationsData } = useQuery({
    queryKey: ['my-active-reservations'],
    queryFn: () => fetchMyReservations('active'),
    enabled: !!user && profile?.role === 'buyer',
  })

  const reservedProjectIds = new Set(
    reservationsData?.data?.map(r => r.unit?.project?.id).filter(Boolean) || []
  )

  useEffect(() => {
    trackPageView('projects')
  }, [])

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchQuery)
    }, 500)
    return () => clearTimeout(timer)
  }, [searchQuery])

  const { data: searchData, isLoading: searchLoading, error: searchError } = useQuery({
    queryKey: ['search-projects', debouncedSearch],
    queryFn: () => searchProjects(debouncedSearch),
    enabled: debouncedSearch.length > 0,
  })

  const { data: projectsData, isLoading: projectsLoading, error: projectsError } = useQuery({
    queryKey: ['projects', filters],
    queryFn: () => fetchProjects({
      location: filters.location || undefined,
      minPrice: filters.minPrice ? parseInt(filters.minPrice) : undefined,
      maxPrice: filters.maxPrice ? parseInt(filters.maxPrice) : undefined,
      bedrooms: filters.bedrooms ? parseInt(filters.bedrooms) : undefined,
    }),
    enabled: !debouncedSearch,
  })

  const isLoading = searchQuery ? searchLoading : projectsLoading
  const error = searchQuery ? searchError : projectsError
  let projects = searchQuery ? (searchData?.data || []) : (projectsData?.data || [])

  useEffect(() => {
    if (searchQuery && searchData?.data && user) {
      trackSearch(searchQuery, searchData.data.length)

      supabase
        .from('search_history')
        .insert({
          query: searchQuery,
          filters: filters,
          results_count: searchData.data.length
        })
        .then(() => {})
    }
  }, [searchQuery, searchData])

  if (filters.sortBy === 'price-low') {
    projects = [...projects].sort((a, b) =>
      (a.price_range_min || 0) - (b.price_range_min || 0)
    )
  } else if (filters.sortBy === 'price-high') {
    projects = [...projects].sort((a, b) =>
      (b.price_range_max || 0) - (a.price_range_max || 0)
    )
  }

  const handleFilterChange = (e) => {
    const { name, value } = e.target
    setFilters(prev => ({ ...prev, [name]: value }))
  }

  const clearFilters = () => {
    setSearchQuery('')
    setFilters({
      location: '',
      minPrice: '',
      maxPrice: '',
      bedrooms: '',
      projectType: '',
      status: '',
      sortBy: 'newest',
    })
  }

  const activeFilterCount = Object.entries(filters).filter(
    ([key, value]) => value !== '' && value !== 'newest' && key !== 'sortBy'
  ).length

  const handleApplySearchHistory = (query, historyFilters) => {
    setSearchQuery(query || '')
    if (historyFilters && Object.keys(historyFilters).length > 0) {
      setFilters(prev => ({ ...prev, ...historyFilters }))
    }
    setShowSearchHistory(false)
  }

  const hasActiveFilters = searchQuery || Object.values(filters).some(v => v !== '' && v !== 'newest')

  const followMutation = useMutation({
    mutationFn: followProject,
    onMutate: async (projectId) => {
      await queryClient.cancelQueries(['projects'])
      await queryClient.cancelQueries(['search-projects'])

      const previousProjects = queryClient.getQueryData(['projects', filters])
      const previousSearch = queryClient.getQueryData(['search-projects', debouncedSearch])

      queryClient.setQueryData(['projects', filters], (old) => {
        if (!old?.data) return old
        return {
          ...old,
          data: old.data.map(p =>
            p.id === projectId ? { ...p, is_following: true } : p
          )
        }
      })

      queryClient.setQueryData(['search-projects', debouncedSearch], (old) => {
        if (!old?.data) return old
        return {
          ...old,
          data: old.data.map(p =>
            p.id === projectId ? { ...p, is_following: true } : p
          )
        }
      })

      return { previousProjects, previousSearch }
    },
    onError: (err, projectId, context) => {
      if (context?.previousProjects) {
        queryClient.setQueryData(['projects', filters], context.previousProjects)
      }
      if (context?.previousSearch) {
        queryClient.setQueryData(['search-projects', debouncedSearch], context.previousSearch)
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries(['projects'])
      queryClient.invalidateQueries(['search-projects'])
    },
  })

  const unfollowMutation = useMutation({
    mutationFn: unfollowProject,
    onMutate: async (projectId) => {
      await queryClient.cancelQueries(['projects'])
      await queryClient.cancelQueries(['search-projects'])

      const previousProjects = queryClient.getQueryData(['projects', filters])
      const previousSearch = queryClient.getQueryData(['search-projects', debouncedSearch])

      queryClient.setQueryData(['projects', filters], (old) => {
        if (!old?.data) return old
        return {
          ...old,
          data: old.data.map(p =>
            p.id === projectId ? { ...p, is_following: false } : p
          )
        }
      })

      queryClient.setQueryData(['search-projects', debouncedSearch], (old) => {
        if (!old?.data) return old
        return {
          ...old,
          data: old.data.map(p =>
            p.id === projectId ? { ...p, is_following: false } : p
          )
        }
      })

      return { previousProjects, previousSearch }
    },
    onError: (err, projectId, context) => {
      if (context?.previousProjects) {
        queryClient.setQueryData(['projects', filters], context.previousProjects)
      }
      if (context?.previousSearch) {
        queryClient.setQueryData(['search-projects', debouncedSearch], context.previousSearch)
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries(['projects'])
      queryClient.invalidateQueries(['search-projects'])
    },
  })

  const handleLikeClick = async (e, project) => {
    e.preventDefault()
    e.stopPropagation()

    if (!user) {
      return
    }

    if (project.is_following) {
      unfollowMutation.mutate(project.id)
    } else {
      followMutation.mutate(project.id)
    }
  }

  return (
    <div className="bg-secondary-50 min-h-screen">
      {/* Pull to Refresh Indicator */}
      {pullDistance > 0 && (
        <div
          className="fixed top-16 left-1/2 -translate-x-1/2 z-50 transition-opacity"
          style={{ opacity: Math.min(pullDistance / 80, 1) }}
        >
          <div className="bg-primary-600 text-white px-4 py-2 rounded-full shadow-lg flex items-center gap-2">
            <svg
              className={`w-4 h-4 ${isRefreshing ? 'animate-spin' : ''}`}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
            <span className="text-sm font-medium">
              {isRefreshing ? 'Refreshing...' : 'Pull to refresh'}
            </span>
          </div>
        </div>
      )}

      {/* Mobile Search Bar */}
      <div className="md:hidden">
        <MobileSearchBar
          value={searchQuery}
          onChange={setSearchQuery}
          onFilterClick={() => setShowMobileFilters(true)}
          activeFiltersCount={activeFilterCount}
        />
      </div>

      <div className="container-custom py-8">
        {/* Header - Desktop Only */}
        <div className="hidden md:block mb-8">
          <h1 className="text-3xl md:text-4xl font-bold text-secondary-900 mb-2">
            Find Your Dream Home
          </h1>
          <p className="text-lg text-secondary-600 mb-6">
            Discover off-plan developments from trusted developers
          </p>

          {/* Search Bar - Desktop */}
          <div className="relative max-w-2xl">
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onFocus={() => setShowSearchHistory(true)}
              placeholder="Search projects by location, title, or features..."
              className="input pl-12 pr-4 py-3 text-lg w-full"
            />
            <svg
              className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-secondary-400"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
              />
            </svg>
            {searchQuery && (
              <button
                onClick={() => setSearchQuery('')}
                className="absolute right-4 top-1/2 transform -translate-y-1/2 text-secondary-400 hover:text-secondary-600"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            )}

            {/* Search History Dropdown */}
            {showSearchHistory && user && (
              <div className="absolute z-50 w-full mt-2 bg-white rounded-lg shadow-lg border border-secondary-200 p-4">
                <div className="flex items-center justify-between mb-3">
                  <h4 className="font-medium text-secondary-900">Search</h4>
                  <button
                    onClick={() => setShowSearchHistory(false)}
                    className="text-secondary-400 hover:text-secondary-600"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
                <SearchHistory onApplySearch={handleApplySearchHistory} />
              </div>
            )}
          </div>
        </div>

        <div className="max-w-7xl mx-auto">
          {/* Filters - Desktop Only */}
          <div className="hidden md:block mb-6">
            {/* Filter Toggle */}
            <button
              onClick={() => setShowFilters(!showFilters)}
              className="w-full btn-outline mb-4 flex items-center justify-between"
            >
              <span>Filters {hasActiveFilters && `(${Object.values(filters).filter(v => v !== '' && v !== 'newest').length})`}</span>
              <svg
                className={`w-5 h-5 transition-transform ${showFilters ? 'rotate-180' : ''}`}
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </button>

            <div className={`card p-6 ${showFilters ? 'block' : 'hidden'}`}>
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-lg font-semibold text-secondary-900">Filters</h2>
                {hasActiveFilters && (
                  <button
                    onClick={clearFilters}
                    className="text-sm text-primary-600 hover:text-primary-700"
                  >
                    Clear all
                  </button>
                )}
              </div>

              <div className="space-y-6">
                {/* Location Filter */}
                <div className="form-group">
                  <label htmlFor="location" className="label">
                    Location
                  </label>
                  <input
                    id="location"
                    name="location"
                    type="text"
                    value={filters.location}
                    onChange={handleFilterChange}
                    className="input"
                    placeholder="e.g., Auckland"
                  />
                </div>

                {/* Price Range */}
                <div className="form-group">
                  <label className="label">Price Range</label>
                  <div className="grid grid-cols-2 gap-2">
                    <input
                      name="minPrice"
                      type="number"
                      value={filters.minPrice}
                      onChange={handleFilterChange}
                      className="input"
                      placeholder="Min"
                    />
                    <input
                      name="maxPrice"
                      type="number"
                      value={filters.maxPrice}
                      onChange={handleFilterChange}
                      className="input"
                      placeholder="Max"
                    />
                  </div>
                </div>

                {/* Bedrooms Filter */}
                <div className="form-group">
                  <label htmlFor="bedrooms" className="label">
                    Bedrooms
                  </label>
                  <select
                    id="bedrooms"
                    name="bedrooms"
                    value={filters.bedrooms}
                    onChange={handleFilterChange}
                    className="input"
                  >
                    <option value="">Any</option>
                    <option value="1">1+</option>
                    <option value="2">2+</option>
                    <option value="3">3+</option>
                    <option value="4">4+</option>
                  </select>
                </div>

                {/* Sort Options */}
                <div className="form-group">
                  <label htmlFor="sortBy" className="label">
                    Sort By
                  </label>
                  <select
                    id="sortBy"
                    name="sortBy"
                    value={filters.sortBy}
                    onChange={handleFilterChange}
                    className="input"
                  >
                    <option value="newest">Newest First</option>
                    <option value="price-low">Price: Low to High</option>
                    <option value="price-high">Price: High to Low</option>
                  </select>
                </div>

                {/* Saved Searches */}
                {user && (
                  <div className="pt-6 border-t border-secondary-200">
                    <SavedSearches
                      currentFilters={filters}
                      onApply={(savedFilters) => {
                        setFilters(prev => ({...prev, ...savedFilters}))
                      }}
                    />
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Projects List */}
          <div>
            {/* Loading State */}
            {isLoading && (
              <div className="flex items-center justify-center py-12">
                <div className="text-center">
                  <div className="spinner spinner-lg text-primary-600 mb-4"></div>
                  <p className="text-secondary-600">Loading projects...</p>
                </div>
              </div>
            )}

            {/* Error State */}
            {error && (
              <div className="alert-error">
                <p>Failed to load projects. Please try again.</p>
              </div>
            )}

            {/* Empty State */}
            {!isLoading && !error && projects.length === 0 && (
              <div className="empty-state">
                <div className="empty-state-icon">
                  <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                  </svg>
                </div>
                <h3 className="empty-state-title">No projects found</h3>
                <p className="empty-state-description">
                  Try adjusting your filters or check back later for new developments.
                </p>
                {hasActiveFilters && (
                  <button onClick={clearFilters} className="btn-primary mt-4">
                    Clear Filters
                  </button>
                )}
              </div>
            )}

            {/* Projects List - Instagram-style edge-to-edge on mobile */}
            {!isLoading && !error && projects.length > 0 && (
              <div className="space-y-0 md:space-y-6 -mx-4 md:mx-0">
                {projects.map((project) => (
                  <ProjectCard
                    key={project.id}
                    project={project}
                    onLikeClick={handleLikeClick}
                    isReserved={reservedProjectIds.has(project.id)}
                    showLikeButton={user && profile?.role === 'buyer'}
                  />
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Mobile Filter Bottom Sheet */}
      <FilterBottomSheet
        isOpen={showMobileFilters}
        onClose={() => setShowMobileFilters(false)}
        filters={filters}
        onFiltersChange={setFilters}
      />
    </div>
  )
}
